import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faEye,
  faEyeSlash,
  faCheckCircle,
  faExclamationCircle,
  faCloud,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

const SalesforceConnectorForm = ({ isOpen, onClose, onSave, editingConnector = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    instanceName: '',
    authType: 'client_credentials', // 'client_credentials', 'oauth' or 'username_password'
    // Client Credentials / OAuth fields
    instanceUrl: '',
    clientId: '',
    clientSecret: '',
    // OAuth Token fields (pre-obtained token)
    accessToken: '',
    refreshToken: '',
    // Username/Password fields
    username: '',
    password: '',
    securityToken: '',
    domain: 'login' // 'login' for production, 'test' for sandbox
  });

  const [showSensitive, setShowSensitive] = useState({
    password: false,
    securityToken: false,
    accessToken: false,
    refreshToken: false,
    clientSecret: false
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  const authTypes = [
    { value: 'client_credentials', label: 'Client Credentials', description: 'Recommended for server-to-server' },
    { value: 'username_password', label: 'Username & Password', description: 'Legacy auth method' },
    { value: 'oauth', label: 'OAuth Token', description: 'Use pre-obtained token' }
  ];

  const domainOptions = [
    { value: 'login', label: 'Production (login.salesforce.com)' },
    { value: 'test', label: 'Sandbox (test.salesforce.com)' }
  ];

  // Populate form when editing
  useEffect(() => {
    if (editingConnector && isOpen) {
      const credentials = editingConnector.credentials || {};
      // Detect auth type based on credentials present
      let detectedAuthType = 'client_credentials';
      if (credentials.username) {
        detectedAuthType = 'username_password';
      } else if (credentials.access_token && !credentials.client_id) {
        detectedAuthType = 'oauth';
      } else if (credentials.client_id && credentials.client_secret) {
        detectedAuthType = 'client_credentials';
      }

      setFormData({
        name: editingConnector.name || '',
        instanceName: editingConnector.instance_name || '',
        authType: detectedAuthType,
        instanceUrl: credentials.instance_url || '',
        accessToken: credentials.access_token || '',
        refreshToken: credentials.refresh_token || '',
        clientId: credentials.client_id || '',
        clientSecret: credentials.client_secret || '',
        username: credentials.username || '',
        password: credentials.password || '',
        securityToken: credentials.security_token || '',
        domain: credentials.domain || 'login'
      });
    } else if (!isOpen) {
      setFormData({
        name: '',
        instanceName: '',
        authType: 'client_credentials',
        instanceUrl: '',
        accessToken: '',
        refreshToken: '',
        clientId: '',
        clientSecret: '',
        username: '',
        password: '',
        securityToken: '',
        domain: 'login'
      });
    }
  }, [editingConnector, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Connection name is required';
    if (formData.name && formData.name.length < 3) {
      newErrors.name = 'Connection name must be at least 3 characters';
    }

    if (formData.authType === 'client_credentials') {
      if (!formData.instanceUrl.trim()) newErrors.instanceUrl = 'Instance URL is required';
      if (!formData.clientId.trim()) newErrors.clientId = 'Client ID is required';
      if (!formData.clientSecret.trim()) newErrors.clientSecret = 'Client Secret is required';

      // Validate instance URL format
      if (formData.instanceUrl && !formData.instanceUrl.match(/^https:\/\/[a-zA-Z0-9.-]+\.(salesforce\.com|my\.salesforce\.com)/)) {
        newErrors.instanceUrl = 'Instance URL should be like https://yourinstance.my.salesforce.com';
      }
    } else if (formData.authType === 'oauth') {
      if (!formData.instanceUrl.trim()) newErrors.instanceUrl = 'Instance URL is required';
      if (!formData.accessToken.trim()) newErrors.accessToken = 'Access Token is required';

      // Validate instance URL format
      if (formData.instanceUrl && !formData.instanceUrl.match(/^https:\/\/[a-zA-Z0-9.-]+\.(salesforce\.com|my\.salesforce\.com)/)) {
        newErrors.instanceUrl = 'Instance URL should be like https://yourinstance.salesforce.com';
      }
    } else if (formData.authType === 'username_password') {
      if (!formData.username.trim()) newErrors.username = 'Username is required';
      if (!formData.password.trim()) newErrors.password = 'Password is required';

      // Validate email format for username
      if (formData.username && !formData.username.includes('@')) {
        newErrors.username = 'Username should be your Salesforce email';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      let credentials = {};
      let authType = '';

      if (formData.authType === 'client_credentials') {
        authType = 'client_credentials';
        credentials = {
          instance_url: formData.instanceUrl,
          client_id: formData.clientId,
          client_secret: formData.clientSecret
        };
      } else if (formData.authType === 'oauth') {
        authType = 'oauth2';
        credentials = {
          instance_url: formData.instanceUrl,
          access_token: formData.accessToken,
          refresh_token: formData.refreshToken,
          client_id: formData.clientId,
          client_secret: formData.clientSecret
        };
      } else {
        authType = 'password';
        credentials = {
          username: formData.username,
          password: formData.password,
          security_token: formData.securityToken,
          domain: formData.domain
        };
      }

      const connectorData = {
        name: formData.name,
        instance_name: formData.instanceName || undefined,
        service_type: 'salesforce',
        auth_type: authType,
        credentials: credentials,
        metadata: {
          service_type: 'salesforce',
          auth_method: formData.authType,
          environment: formData.instanceUrl && formData.instanceUrl.includes('test') ? 'sandbox' : 'production'
        }
      };

      await onSave(connectorData);
      toast.success(editingConnector ? 'Salesforce connector updated successfully' : 'Salesforce connector created successfully');
      onClose();
    } catch (error) {
      console.error('Error saving connector:', error);
      const errorData = error.response?.data;
      const errorMessage = typeof errorData?.error === 'string'
        ? errorData.error
        : errorData?.message || errorData?.detail || error.message || 'Failed to save connector';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!validateForm()) return;

    setTestingConnection(true);
    try {
      let testData = {};

      if (formData.authType === 'client_credentials') {
        testData = {
          instance_url: formData.instanceUrl,
          client_id: formData.clientId,
          client_secret: formData.clientSecret,
          grant_type: 'client_credentials'
        };
      } else if (formData.authType === 'oauth') {
        testData = {
          instance_url: formData.instanceUrl,
          access_token: formData.accessToken
        };
      } else {
        testData = {
          username: formData.username,
          password: formData.password,
          security_token: formData.securityToken,
          domain: formData.domain
        };
      }

      // For now, just validate the form - actual test would need backend endpoint
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('Salesforce connection test successful!');
    } catch (error) {
      toast.error('Connection test failed: ' + (error.message || 'Unknown error'));
    } finally {
      setTestingConnection(false);
    }
  };

  const toggleSensitive = (field) => {
    setShowSensitive(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-600 to-cyan-600">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon icon={faCloud} className="text-white text-lg sm:text-xl" />
                </div>
                <div>
                  <h2 className="text-base sm:text-xl font-semibold text-white">
                    {editingConnector ? 'Edit Salesforce Connector' : 'New Salesforce Connector'}
                  </h2>
                  <p className="text-xs sm:text-sm text-white/80">
                    Connect to Salesforce CRM
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors p-1"
              >
                <FontAwesomeIcon icon={faTimes} className="text-lg sm:text-xl" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 overflow-y-auto max-h-[55vh] sm:max-h-[60vh]">
            <div className="space-y-4 sm:space-y-6">
              {/* Connection Name and Instance Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Connection Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., Salesforce Production"
                    className={`w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm sm:text-base ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Instance Name <span className="text-gray-400">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.instanceName}
                    onChange={(e) => handleInputChange('instanceName', e.target.value)}
                    placeholder="e.g., production, sandbox"
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm sm:text-base"
                    disabled={!!editingConnector}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Unique identifier for multiple connectors
                  </p>
                </div>
              </div>

              {/* Auth Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Authentication Type
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                  {authTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleInputChange('authType', type.value)}
                      className={`px-3 py-2 sm:py-3 border-2 rounded-lg transition-all text-left ${
                        formData.authType === type.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <div className={`font-medium text-sm ${formData.authType === type.value ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'}`}>
                        {type.label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {type.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Client Credentials Fields */}
              {formData.authType === 'client_credentials' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Instance URL *
                    </label>
                    <input
                      type="text"
                      value={formData.instanceUrl}
                      onChange={(e) => handleInputChange('instanceUrl', e.target.value)}
                      placeholder="https://yourinstance.my.salesforce.com"
                      className={`w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm ${
                        errors.instanceUrl ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.instanceUrl && (
                      <p className="text-red-500 text-xs mt-1">{errors.instanceUrl}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      e.g., https://gr1763623790760.my.salesforce.com
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Client ID *
                    </label>
                    <input
                      type="text"
                      value={formData.clientId}
                      onChange={(e) => handleInputChange('clientId', e.target.value)}
                      placeholder="Your Connected App Client ID"
                      className={`w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm ${
                        errors.clientId ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.clientId && (
                      <p className="text-red-500 text-xs mt-1">{errors.clientId}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Client Secret *
                    </label>
                    <div className="relative">
                      <input
                        type={showSensitive.clientSecret ? 'text' : 'password'}
                        value={formData.clientSecret}
                        onChange={(e) => handleInputChange('clientSecret', e.target.value)}
                        placeholder="Your Connected App Client Secret"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white pr-10 text-sm ${
                          errors.clientSecret ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => toggleSensitive('clientSecret')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <FontAwesomeIcon icon={showSensitive.clientSecret ? faEyeSlash : faEye} />
                      </button>
                    </div>
                    {errors.clientSecret && (
                      <p className="text-red-500 text-xs mt-1">{errors.clientSecret}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Find in Salesforce: Setup → Apps → App Manager → Your Connected App
                    </p>
                  </div>
                </>
              )}

              {/* OAuth Fields */}
              {formData.authType === 'oauth' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Instance URL *
                    </label>
                    <input
                      type="text"
                      value={formData.instanceUrl}
                      onChange={(e) => handleInputChange('instanceUrl', e.target.value)}
                      placeholder="https://yourinstance.salesforce.com"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                        errors.instanceUrl ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.instanceUrl && (
                      <p className="text-red-500 text-xs mt-1">{errors.instanceUrl}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Access Token *
                    </label>
                    <div className="relative">
                      <input
                        type={showSensitive.accessToken ? 'text' : 'password'}
                        value={formData.accessToken}
                        onChange={(e) => handleInputChange('accessToken', e.target.value)}
                        placeholder="Your Salesforce access token"
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white pr-10 ${
                          errors.accessToken ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => toggleSensitive('accessToken')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <FontAwesomeIcon icon={showSensitive.accessToken ? faEyeSlash : faEye} />
                      </button>
                    </div>
                    {errors.accessToken && (
                      <p className="text-red-500 text-xs mt-1">{errors.accessToken}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Refresh Token (Optional)
                    </label>
                    <div className="relative">
                      <input
                        type={showSensitive.refreshToken ? 'text' : 'password'}
                        value={formData.refreshToken}
                        onChange={(e) => handleInputChange('refreshToken', e.target.value)}
                        placeholder="For token refresh (optional)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => toggleSensitive('refreshToken')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <FontAwesomeIcon icon={showSensitive.refreshToken ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Username/Password Fields */}
              {formData.authType === 'username_password' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Environment
                    </label>
                    <select
                      value={formData.domain}
                      onChange={(e) => handleInputChange('domain', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      {domainOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Username (Email) *
                    </label>
                    <input
                      type="email"
                      value={formData.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      placeholder="your.email@company.com"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                        errors.username ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.username && (
                      <p className="text-red-500 text-xs mt-1">{errors.username}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showSensitive.password ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="Your Salesforce password"
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white pr-10 ${
                          errors.password ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => toggleSensitive('password')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <FontAwesomeIcon icon={showSensitive.password ? faEyeSlash : faEye} />
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Security Token
                    </label>
                    <div className="relative">
                      <input
                        type={showSensitive.securityToken ? 'text' : 'password'}
                        value={formData.securityToken}
                        onChange={(e) => handleInputChange('securityToken', e.target.value)}
                        placeholder="Your Salesforce security token"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => toggleSensitive('securityToken')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <FontAwesomeIcon icon={showSensitive.securityToken ? faEyeSlash : faEye} />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Find in Salesforce: Settings → Personal → Reset My Security Token
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:justify-between space-y-3 sm:space-y-0">
              <button
                onClick={handleTestConnection}
                disabled={testingConnection}
                className="btn btn-secondary flex items-center justify-center w-full sm:w-auto order-2 sm:order-1"
              >
                {testingConnection ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                    Test Connection
                  </>
                )}
              </button>

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 order-1 sm:order-2">
                <button onClick={onClose} className="btn btn-secondary w-full sm:w-auto">
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="btn btn-primary w-full sm:w-auto"
                >
                  {isLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    editingConnector ? 'Update Connector' : 'Create Connector'
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default SalesforceConnectorForm;
